import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../../components/Input'
import { Checkbox } from '../../components/Checkbox'
import { useOnboardingStore } from '../../store/onboardingStore'
import { isValidItalianTaxCode, isValidEmail } from '../../utils/validation'
import type { PersonalData } from '../../types'

const ID_DOCUMENT_TYPES = ['Carta Identità', 'Passaporto', 'Patente Auto', 'Permesso di Soggiorno']

/**
 * The Italy-only Step 2 rules the shared step must respect before it enables
 * "Avanti". Keeping this here (rather than in Step2PersonalData) means Italy's
 * validation rules live with Italy's fields; the shared step just asks.
 *
 * Codice fiscale is MANDATORY — unless the patient ticks the "I don't have a
 * codice fiscale (foreigner / resident abroad)" checkbox. A value that IS
 * entered must be a valid codice fiscale, so typos get caught at entry.
 */
export function isItalyPersonalDataValid(personalData: PersonalData): boolean {
  const taxCode = personalData.codiceFiscale?.trim()
  const taxCodeOk = taxCode ? isValidItalianTaxCode(taxCode) : !!personalData.codiceFiscaleAbsent
  const gpEmail = personalData.generalPractitionerEmail?.trim()
  const gpEmailOk = !gpEmail || isValidEmail(gpEmail)
  return taxCodeOk && gpEmailOk
}

/**
 * Italy-only personal fields, rendered inside Step 2 for the IT org:
 *   - codice fiscale (validated: syntax + CIN check character)
 *   - medico di base (GP name + phone)
 *   - identity document (type / number / issued-by / issued-on)
 *
 * Self-contained: reads/writes the onboarding store directly. The shared
 * Step2PersonalData only renders `{isItaly && <ItalyPersonalFields />}`, so the
 * DE/ES flow never touches any of this.
 */
export function ItalyPersonalFields() {
  const { t } = useTranslation()
  const { personalData, setPersonalData } = useOnboardingStore()
  const [taxCodeError, setTaxCodeError] = useState<string | undefined>()

  // Validate on blur, not per keystroke — otherwise the field screams "invalid"
  // at the patient while they are still typing the 16th character.
  const handleTaxCodeBlur = () => {
    const taxCode = (personalData.codiceFiscale || '').trim()
    if (taxCode) setPersonalData({ codiceFiscale: taxCode })
    setTaxCodeError(
      !taxCode || isValidItalianTaxCode(taxCode) ? undefined : t('step2.codiceFiscaleInvalid')
    )
  }

  return (
    <>
      <div>
        <Input
          id="codiceFiscale"
          type="text"
          label={t('step2.codiceFiscale')}
          placeholder={t('step2.codiceFiscalePlaceholder')}
          value={personalData.codiceFiscale || ''}
          onChange={(e) => {
            setPersonalData({ codiceFiscale: e.target.value.toUpperCase() })
            if (taxCodeError) setTaxCodeError(undefined)
          }}
          onBlur={handleTaxCodeBlur}
          error={taxCodeError}
        />
        {/* Mandatory-unless-exempt: empty codice fiscale requires this declaration */}
        {!(personalData.codiceFiscale || '').trim() && (
          <div className="mt-2">
            <Checkbox
              id="codiceFiscaleAbsent"
              checked={personalData.codiceFiscaleAbsent || false}
              onChange={(e) => setPersonalData({ codiceFiscaleAbsent: e.target.checked })}
              label={t('step2.noCodiceFiscale')}
            />
          </div>
        )}
      </div>

      {/* Medico di base (GP) — name + phone. Optional. */}
      <Input
        id="gpName"
        type="text"
        label={t('step2.gpName')}
        placeholder={t('step2.gpNamePlaceholder')}
        value={personalData.generalPractitionerName || ''}
        onChange={(e) => setPersonalData({ generalPractitionerName: e.target.value })}
      />
      <Input
        id="gpPhone"
        type="tel"
        label={t('step2.gpPhone')}
        placeholder={t('step2.gpPhonePlaceholder')}
        value={personalData.generalPractitionerPhone || ''}
        onChange={(e) => setPersonalData({ generalPractitionerPhone: e.target.value })}
      />
      <Input
        id="gpEmail"
        type="email"
        label={t('step2.gpEmail')}
        placeholder={t('step2.gpEmailPlaceholder')}
        value={personalData.generalPractitionerEmail || ''}
        onChange={(e) => setPersonalData({ generalPractitionerEmail: e.target.value })}
        onBlur={() => {
          const v = (personalData.generalPractitionerEmail || '').trim()
          if (v !== personalData.generalPractitionerEmail) setPersonalData({ generalPractitionerEmail: v })
        }}
      />

      {/* Identity document — type + number + issuer + issue date. */}
      <div>
        <label htmlFor="idDocType" className="block text-body font-medium text-foreground mb-1.5">
          {t('step2.idDocumentType')}
        </label>
        <select
          id="idDocType"
          value={personalData.idDocumentType || ''}
          onChange={(e) => setPersonalData({ idDocumentType: e.target.value })}
          className="w-full px-4 py-3 text-body border border-border rounded-input outline-none bg-white focus:border-primary"
        >
          <option value="">{t('step2.idDocumentTypePlaceholder')}</option>
          {ID_DOCUMENT_TYPES.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>
      <Input
        id="idDocNumber"
        type="text"
        label={t('step2.idDocumentNumber')}
        value={personalData.idDocumentNumber || ''}
        onChange={(e) => setPersonalData({ idDocumentNumber: e.target.value })}
      />
      <Input
        id="idDocIssuedBy"
        type="text"
        label={t('step2.idDocumentIssuedBy')}
        placeholder={t('step2.idDocumentIssuedByPlaceholder')}
        value={personalData.idDocumentIssuedBy || ''}
        onChange={(e) => setPersonalData({ idDocumentIssuedBy: e.target.value })}
      />
      <Input
        id="idDocIssuedOn"
        type="date"
        label={t('step2.idDocumentIssuedOn')}
        value={personalData.idDocumentIssuedOn || ''}
        onChange={(e) => setPersonalData({ idDocumentIssuedOn: e.target.value })}
      />
    </>
  )
}
